import { useEffect, useState } from "react";
import { Toast, ToastBar, useToasterStore } from "react-hot-toast";

interface ToastBarWithProgressProps {
  toast: Toast;
}

const calculateProgress = (toast: Toast, pausedAt: number | null | undefined): number => {
  if (!toast.duration || toast.duration === Infinity) return 100;

  const now = Date.now();
  const elapsed = now - toast.createdAt;

  let adjustedElapsed = elapsed - (toast.pauseDuration || 0);

  if (pausedAt != null) {
    adjustedElapsed -= now - pausedAt;
  }

  const progress = Math.max(0, 100 - (adjustedElapsed / toast.duration) * 100);

  return progress;
};

export default function ToastBarWithProgress({ toast }: ToastBarWithProgressProps) {
  const { pausedAt } = useToasterStore();
  const [progress, setProgress] = useState(() => calculateProgress(toast, pausedAt ?? null));

  useEffect(() => {
    if (!toast.duration || toast.duration === Infinity) {
      return;
    }

    let animationFrameId: number;
    let isActive = true;

    const updateProgress = () => {
      if (!isActive) return;

      const newProgress = calculateProgress(toast, pausedAt ?? null);
      setProgress(newProgress);

      if (newProgress > 0) {
        animationFrameId = requestAnimationFrame(updateProgress);
      }
    };

    animationFrameId = requestAnimationFrame(updateProgress);

    return () => {
      isActive = false;
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [toast, pausedAt]);

  const showProgressBar = toast.duration && toast.duration !== Infinity;

  return (
    <ToastBar toast={toast}>
      {({ icon, message }) => (
        <>
          {icon}
          {message}
          {showProgressBar && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden rounded-b-[6px]">
              <div
                className="h-full bg-stroke-base will-change-[width]"
                style={{
                  width: `${progress}%`,
                }}
                role="progressbar"
                aria-valuenow={Math.round(progress)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Toast remaining time"
              />
            </div>
          )}
        </>
      )}
    </ToastBar>
  );
}
